import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "@/lib/api";
import type { AuthenticatedUser } from "@/lib/types";

interface AuthState {
  user: AuthenticatedUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Probe /me on first load. The session cookie (if present) gives us identity.
  useEffect(() => {
    let cancelled = false;
    api.get<AuthenticatedUser>("/api/v1/auth/me")
      .then((u) => { if (!cancelled) setUser(u); })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function login(email: string, password: string) {
    const u = await api.post<AuthenticatedUser>("/api/v1/auth/login", { email, password });
    setUser(u);
  }

  async function logout() {
    try {
      await api.post<void>("/api/v1/auth/logout");
    } catch (e) {
      // Ignore — cookies are cleared by the server response either way.
      if (!(e instanceof ApiError)) throw e;
    }
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
